/**
 * Metric Insight adapter.
 *
 * Turns a MetricInsight's (viz_type, viz_config) + the SQL rows returned by
 * /api/metric-insights/with_data/ into the concrete props the matching generic
 * viz component expects. This is the single layer that replaces the bespoke
 * per-insight .vue transforms.
 *
 * Returns { component, props } where component is the registry key the
 * dispatcher (MetricInsightsWidget) maps to a Vue component.
 *
 * viz_config schemas
 * ------------------
 * chart_v5: {
 *   label_field: string,                         // x-axis category column
 *   series: [{ value_field, label, color, type('bar'|'line'), y_axis('left'|'right'), offset }],
 *   chart_config: { ...Chart_V5 passthrough }    // chartType, formatting, title, axes, legend
 * }
 * table: { columnDefs: [{ field, headerName, valueFormatter?, width? }] }   // rowData = rows
 * sankey: {
 *   source_field, target_field, value_field,
 *   stage_labels: [string],                      // ordered Sankey stages
 *   config: { ...Sankey_Diagram passthrough }
 * }
 * heatmap: { x_field, y_field, value_field, chart_config: { ...HexMap passthrough } }
 */

import _ from 'lodash'

/**
 * Group rows by labelField, summing valueField per label (sorted).
 * Mirrors return_simple_data_series in utils/common.js.
 */
function seriesFromRows(rows, labelField, valueField) {
  const uniq = [...new Set(rows.map(r => r[labelField]))]
  // Numeric label columns (e.g. day_num 0..30) must sort numerically — JS's
  // default Array.sort is lexical, which would order 1,10,11,...,2,20,... and
  // produce a zigzag line. Fall back to locale string sort for categorical labels.
  const allNumeric = uniq.length > 0 && uniq.every(v => v !== null && v !== '' && !isNaN(Number(v)))
  const labels = allNumeric
    ? uniq.sort((a, b) => Number(a) - Number(b))
    : uniq.sort()
  const values = labels.map(label =>
    _.sumBy(rows.filter(r => r[labelField] === label), r => r[valueField] || 0)
  )
  return { labels, values }
}

const DEFAULT_COLORS = [
  'rgba(255, 145, 73, 0.6)',
  'rgba(96, 181, 255, 0.6)',
  'rgba(0, 150, 0, 0.5)',
  'rgba(150, 150, 150, 0.5)',
  'rgba(0, 0, 150, 0.4)',
  'rgba(192, 0, 0, 0.4)',
]

function buildChartV5(viz_config, rows) {
  const labelField = viz_config.label_field
  const seriesSpecs = viz_config.series || []

  // Labels come from the first series' grouping (all series share the x axis).
  let labels = []
  const series = seriesSpecs.map((spec, i) => {
    const s = seriesFromRows(rows, labelField, spec.value_field)
    if (i === 0) labels = s.labels
    return {
      values: s.values,
      config: {
        label: spec.label || spec.value_field,
        backgroundColor: spec.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
        type: spec.type || 'bar',
        yAxis: spec.y_axis || 'left',
        offset: spec.offset != null ? spec.offset : 0,
      },
    }
  })

  // The MetricInsightsWidget card header already shows the insight name, so
  // suppress the chart's own in-SVG title/subtitle to avoid a duplicate heading.
  const chart_config = { ...(viz_config.chart_config || {}), title: '', subtitle: '' }

  return {
    component: 'chart_v5',
    props: {
      chart_data: { series, labels },
      chart_config,
    },
  }
}

function buildTable(viz_config, rows) {
  return {
    component: 'table',
    props: {
      columnDefs: viz_config.columnDefs || [],
      rowData: rows,
    },
  }
}

function buildSankey(viz_config, rows) {
  const { source_field, target_field, value_field, stage_labels = [] } = viz_config

  // Build the node index from source + target columns (preserve first-seen order).
  const nodeMap = {}
  const nodes = []
  const ensureNode = name => {
    if (nodeMap[name] == null) {
      nodeMap[name] = { index: nodes.length, name }
      nodes.push({ name })
    }
    return nodeMap[name].index
  }

  // Aggregate links by (source, target).
  const linkAgg = {}
  rows.forEach(r => {
    const src = r[source_field]
    const tgt = r[target_field]
    if (src == null || tgt == null) return
    const key = `${src}|${tgt}`
    if (!linkAgg[key]) linkAgg[key] = { source: src, target: tgt, value: 0 }
    linkAgg[key].value += Number(r[value_field]) || 0
  })

  const links = Object.values(linkAgg)
    .filter(l => l.value > 0)
    .map(l => ({
      source: ensureNode(l.source),
      target: ensureNode(l.target),
      value: l.value,
    }))

  return {
    component: 'sankey',
    props: {
      chartData: { nodes, links, stageLabels: stage_labels },
      config: viz_config.config || {},
    },
  }
}

function buildHeatmap(viz_config, rows) {
  const { x_field, y_field, value_field } = viz_config
  const chartData = rows.map(r => ({
    x: r[x_field],
    y: r[y_field],
    value: Number(r[value_field]) || 0,
  }))
  return {
    component: 'heatmap',
    props: {
      chartData,
      chartConfig: viz_config.chart_config || {},
    },
  }
}

/**
 * pie: {
 *   label_field: string,                  // slice label column
 *   value_field: string,                  // slice value column
 *   colors?: [string],                    // optional per-slice colors (in row order)
 *   chart_config: { ...Pie_Chart passthrough }
 * }
 * Rows are taken in the order the backend returns them (the DA's ORDER BY),
 * summing value_field per label.
 */
function buildPie(viz_config, rows) {
  const labelField = viz_config.label_field
  const valueField = viz_config.value_field
  const labels = []
  const values = []
  rows.forEach(r => {
    const label = r[labelField]
    const i = labels.indexOf(label)
    const v = Number(r[valueField]) || 0
    if (i === -1) {
      labels.push(label)
      values.push(v)
    } else {
      values[i] += v
    }
  })
  return {
    component: 'pie',
    props: {
      chart_data: { labels, values, colors: viz_config.colors || null },
      chart_config: viz_config.chart_config || {},
    },
  }
}


/**
 * chart_v6 — the agent-first chart. Config is field-mapping (same skeleton as
 * chart_v5, so a scratchpad chart saves as an insight verbatim); the component
 * owns all styling. viz_config:
 *   { type: 'bar'|'line'|'area'|'scatter'|'donut', label_field,
 *     series: [{ value_field, name? }], format?, stacked?, goal?,
 *     highlight?, title?, subtitle? }
 */
function buildChartV6(viz_config, rows) {
  const labelField = viz_config.label_field
  const specs = viz_config.series || []
  let labels = []
  const series = specs.map((sp, i) => {
    const s = seriesFromRows(rows, labelField, sp.value_field)
    if (i === 0) labels = s.labels
    return { name: sp.name || sp.value_field, values: s.values }
  })
  return {
    component: 'chart_v6',
    props: {
      spec: {
        type: viz_config.type || 'bar',
        title: viz_config.title,
        subtitle: viz_config.subtitle,
        x: labels,
        series,
        format: viz_config.format,
        stacked: Boolean(viz_config.stacked),
        goal: viz_config.goal,
        highlight: viz_config.highlight,
      },
    },
  }
}

const BUILDERS = {
  chart_v5: buildChartV5,
  chart_v6: buildChartV6,
  table: buildTable,
  sankey: buildSankey,
  heatmap: buildHeatmap,
  pie: buildPie,
}

/**
 * @param {string} vizType
 * @param {object} vizConfig
 * @param {Array<object>} rows  - SQL result rows (lowercased keys)
 * @returns {{component: string, props: object}}
 */
export function buildVizProps(vizType, vizConfig, rows) {
  const builder = BUILDERS[vizType]
  if (!builder) {
    return { component: 'table', props: { columnDefs: [], rowData: rows || [] } }
  }
  return builder(vizConfig || {}, Array.isArray(rows) ? rows : [])
}
